// Classify a lesson's `videoRef` so the player knows how to render it.
//
// The field historically only ever held a Bunny Stream GUID. To let admins
// paste a YouTube link into the same field, we sniff the value:
//   - a YouTube URL (any common shape) or a bare 11-char video id → youtube
//   - anything else → bunny (unchanged; existing refs keep working)
//
// Pure + SDK-free so it is unit-testable and shared by the player and the
// admin editor hint.

export type VideoRef =
  | { kind: "none" }
  | { kind: "youtube"; id: string }
  | { kind: "bunny"; id: string }
  | { kind: "file"; url: string };

// A plain video file the admin uploaded (Firebase Storage) or any direct link.
const FILE_URL = /^https?:\/\/\S+$/i;
const FILE_EXT = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

// 11-char YouTube id alphabet.
const YT_ID = /^[A-Za-z0-9_-]{11}$/;

// Matches v=, youtu.be/, /embed/, /shorts/, /live/ forms and captures the id.
const YT_URL = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

export function parseVideoRef(raw: string | undefined | null): VideoRef {
  const s = (raw ?? "").trim();
  if (!s) return { kind: "none" };

  if (/youtube\.com|youtu\.be/.test(s)) {
    const m = s.match(YT_URL);
    if (m) return { kind: "youtube", id: m[1] };
    // Looks like a YouTube URL but no id parsed - fall through to bunny so we
    // never render a broken embed; admin will see it doesn't play and fix it.
  }

  // A bare, slash-free 11-char token is almost certainly a YouTube id (Bunny
  // GUIDs are 36 chars with hyphens). Slashes disqualify it.
  if (!s.includes("/") && YT_ID.test(s)) return { kind: "youtube", id: s };

  // Uploaded file: a real URL that either ends in a video extension or lives in
  // our own storage bucket (its download URLs carry no extension).
  if (FILE_URL.test(s) && (FILE_EXT.test(s) || s.includes("firebasestorage.googleapis.com"))) {
    return { kind: "file", url: s };
  }

  return { kind: "bunny", id: s };
}

export function youTubeEmbedUrl(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`;
}
